import { useQuery, useQueryClient } from '@tanstack/react-query';

import { CalendarEventType } from './calendar-event-type';

const CALENDAR_VIEW_LOCAL_STORAGE_KEY = 'calendar-view-state';

export enum EventCalendarView {
    DAY_GRID_MONTH = 'dayGridMonth',
    TIME_GRID_WEEK = 'timeGridWeek',
    TIME_GRID_DAY = 'timeGridDay',
}

export type CalendarTab = 'calendar' | 'meetingMinutes';

export type NotesFilter = 'all' | 'withNotes';

export type EventCalendarViewState = {
    calendarView: EventCalendarView;
    showWeekends: boolean;
    // null = no segment selected = all events shown
    eventTypeFilter: CalendarEventType | null;
    activeTab: CalendarTab;
    // Meeting Minutes tab reading list segment; 'all' is the default and a segment is always selected
    notesFilter: NotesFilter;
};

const DEFAULT_EVENT_CALENDAR_VIEW_STATE: EventCalendarViewState = {
    calendarView: EventCalendarView.TIME_GRID_WEEK,
    showWeekends: false,
    eventTypeFilter: null,
    activeTab: 'calendar',
    notesFilter: 'all',
};

const getLocalStorageEventCalendarViewState = () => {
    try {
        const value = localStorage.getItem(CALENDAR_VIEW_LOCAL_STORAGE_KEY);
        return value
            ? {
                  ...DEFAULT_EVENT_CALENDAR_VIEW_STATE,
                  ...JSON.parse(value),
              }
            : DEFAULT_EVENT_CALENDAR_VIEW_STATE;
    } catch (error) {
        return DEFAULT_EVENT_CALENDAR_VIEW_STATE;
    }
};

const useEventCalendarViewQuery = () => {
    return useQuery({
        queryKey: [CALENDAR_VIEW_LOCAL_STORAGE_KEY],
        queryFn: getLocalStorageEventCalendarViewState,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
    });
};

export const useEventCalendarViewState = (): {
    eventCalendarViewState: EventCalendarViewState;
    setEventCalendarViewState: (partialState: Partial<EventCalendarViewState>) => void;
} => {
    const queryClient = useQueryClient();
    const eventCalendarViewStateQuery = useEventCalendarViewQuery();

    return {
        eventCalendarViewState: eventCalendarViewStateQuery.data ?? getLocalStorageEventCalendarViewState(),
        setEventCalendarViewState: (partialState: Partial<EventCalendarViewState>) => {
            localStorage.setItem(
                CALENDAR_VIEW_LOCAL_STORAGE_KEY,
                JSON.stringify({
                    ...DEFAULT_EVENT_CALENDAR_VIEW_STATE,
                    ...eventCalendarViewStateQuery.data,
                    ...partialState,
                }),
            );
            queryClient.invalidateQueries({
                queryKey: [CALENDAR_VIEW_LOCAL_STORAGE_KEY],
            });
        },
    } as const;
};
